import { AbstractService } from 'src/services/AbstractService';
import { config } from 'src/lib/config';
import { ServiceError } from 'src/lib/errors';

export interface MpvLookupResult {
    productKey: string;
    culture: string;
}

class MsxServiceApi extends AbstractService {
    /**
     * Resolves an MPV id (e.g. "notebooks") to a product key. Caller passes one or more
     * cultures (MSX requires at least one); the response includes only the cultures where
     * the MPV resolves. Returns the first matching culture/productKey pair, or null.
     *
     * MSX response shape: { "<culture>": "PRD-..." } — only cultures with a hit are present.
     */
    public async getProductKeyByMpvId(
        mpvId: string,
        cultures: string[],
        signal?: AbortSignal,
    ): Promise<MpvLookupResult | null> {
        const url = `api/${this.version}/tenant/vistaprint/mpvs/${encodeURIComponent(mpvId)}`;

        const searchParams = new URLSearchParams();

        searchParams.append('requestor', config.appName);
        cultures.forEach((c) => searchParams.append('cultures', c));

        try {
            const response = await this.api
                .get(url, { searchParams, signal })
                .json<Record<string, string>>();

            const entries = Object.entries(response).filter(([, prd]) => !!prd);

            if (entries.length === 0) return null;

            const orderedHit = cultures
                .map((c) => entries.find(([k]) => k.toLowerCase() === c.toLowerCase()))
                .find((entry) => !!entry)
                ?? entries[0];

            return { culture: orderedHit[0], productKey: orderedHit[1] };
        } catch (e: unknown) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to resolve MPV ID to product key',
                info: (e as Error).message,
                url,
                mpvId,
                cultures: cultures.join(','),
            });
        }
    }
}

export const MsxService = new MsxServiceApi(config.services.msxServiceApi);
